import React, { useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

// Helper to get S3 URL
export const getS3Url = (s3Key: string | undefined | null): string => {
    if (!s3Key) return '';
    const s3BaseUrl = 'https://podscope.s3.amazonaws.com';
    const cleanKey = s3Key.startsWith('/') ? s3Key.slice(1) : s3Key;
    return `${s3BaseUrl}/${cleanKey}`;
};

// Format numbers for display (Spanish format: comma as decimal separator)
export const formatNumber = (num: number): string => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1).replace('.', ',')}M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
};

export const ChartRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    marginBottom: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    },
    '@media (min-width: 600px)': {
        gap: '16px',
        borderRadius: '22px',
        padding: '12px 16px',
        marginBottom: '10px',
    },
}));

export const RankNumber = styled(Typography)(() => ({
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a2e',
    fontFamily: '"DM Serif Display", serif',
    width: '24px',
    textAlign: 'left',
    '@media (min-width: 600px)': {
        fontSize: '1.70rem',
        width: '24px',
    },
}));

export const MetricText = styled(Typography)(() => ({
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1a1a2e',
    lineHeight: 1.3,
    fontFamily: '"DM Serif Display", serif',
    '@media (min-width: 600px)': {
        fontSize: '1.70rem',
        lineHeight: 1.2,
    },
}));

// Loading skeleton component
export const RankingLoadingSkeleton: React.FC<{
    count?: number;
    showPositionChange?: boolean;
    backgroundColor?: string;
}> = ({
    count = 10,
    showPositionChange = false,
    backgroundColor = 'rgba(255, 255, 255, 0.95)',
}) => (
    <>
        {Array.from({ length: count }).map((_, index) => (
            <Box
                key={index}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '12px', sm: '16px' },
                    padding: { xs: '12px 14px', sm: '14px 20px' },
                    backgroundColor,
                    borderRadius: '12px',
                    marginBottom: { xs: '8px', sm: '10px' },
                }}
            >
                <Skeleton
                    variant="text"
                    sx={{ minWidth: { xs: '24px', sm: '32px' }, height: { xs: 24, sm: 28 } }}
                />
                {showPositionChange && (
                    <Skeleton
                        variant="rounded"
                        sx={{
                            width: { xs: 36, sm: 50 },
                            height: { xs: 20, sm: 22 },
                            borderRadius: '12px',
                            flexShrink: 0,
                        }}
                    />
                )}
                <Skeleton
                    variant="rounded"
                    sx={{
                        width: { xs: 52, sm: 64 },
                        height: { xs: 52, sm: 64 },
                        borderRadius: { xs: '12px', sm: '14px' },
                        flexShrink: 0,
                    }}
                />
                <Skeleton variant="text" sx={{ flex: 1, height: { xs: 20, sm: 24 } }} />
                <Skeleton
                    variant="text"
                    sx={{
                        minWidth: { xs: '50px', sm: '70px' },
                        height: { xs: 20, sm: 24 },
                        display: { xs: 'none', sm: 'block' },
                    }}
                />
            </Box>
        ))}
    </>
);

// Image/Avatar component
export const ItemImage: React.FC<{
    src?: string;
    alt: string;
    fallbackLetter?: string;
    /** Square side length in px; overrides default 50/52 when set. */
    sizePx?: number;
}> = ({ src, alt, fallbackLetter, sizePx }) => {
    const [imgSrc, setImgSrc] = useState<string | undefined>(src);

    const handleError = () => {
        // Only try the alternate extension once (src → fallback → undefined).
        // Swapping back and forth would cause an infinite request loop.
        if (imgSrc === src && src?.endsWith('.jpg')) {
            setImgSrc(src.replace(/\.jpg$/, '.png'));
        } else if (imgSrc === src && src?.endsWith('.png')) {
            setImgSrc(src.replace(/\.png$/, '.jpg'));
        } else {
            setImgSrc(undefined);
        }
    };

    const dim =
        sizePx != null
            ? {
                  width: sizePx,
                  height: sizePx,
                  borderRadius: `${Math.max(12, Math.round(sizePx * 0.18))}px`,
              }
            : {
                  width: { xs: 50, sm: 52 },
                  height: { xs: 50, sm: 52 },
                  borderRadius: { xs: '12px', sm: '12px' },
              };
    const imgRadius =
        sizePx != null
            ? `${Math.max(12, Math.round(sizePx * 0.18))}px`
            : { xs: '12px', sm: '12px' };

    return (
        <Box
            sx={{
                ...dim,
                flexShrink: 0,
                backgroundColor: '#f5f5f5',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {imgSrc ? (
                <Box
                    component="img"
                    src={imgSrc}
                    alt={alt}
                    onError={handleError}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        // border-radius on the img itself avoids overflow:hidden on the
                        // parent — html2canvas silently drops images inside
                        // overflow:hidden + border-radius containers (known bug)
                        borderRadius: imgRadius,
                        display: 'block',
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        fontSize:
                            sizePx != null
                                ? `${Math.round(sizePx * 0.38)}px`
                                : { xs: '1rem', sm: '1.25rem' },
                        fontWeight: 700,
                        color: '#999',
                        fontFamily: '"DM Serif Display", serif',
                    }}
                >
                    {fallbackLetter || alt.charAt(0)}
                </Typography>
            )}
        </Box>
    );
};
